import React, { type PropsWithChildren } from 'react';

import {
  AnnouncementBanner,
  Box,
  Container,
  LayoutContext,
  Pingdom,
  GlobalCSS,
  SiteFooter,
  Combobox,
  Link,
  ThemeProvider,
  NavBlogProvider,
} from '@lucid/components';
import { theme } from '@lucid/theme';
import {
  useSiteMetadata,
  useNavMenu,
  useNavFooterMenu,
  useAnnouncementBanner,
  useSmartlingStrings,
} from '@lucid/hooks';
import { getLanguage } from '@lucid/utils';
import styled from '@emotion/styled';

import LearningCampusNav from 'components/learning-campus-nav/learning-campus-nav';
import type { Lucid } from 'types/types';
import logo from 'assets/svgs/LearningCampusLogo.svg';

export interface ProductSelectValue {
  label: string;
  value: Lucid.Site.Product;
}

interface LearningCampusLayoutProps extends Lucid.Context.Layout {
  children: React.ReactNode | React.ReactNode[];
  product: Lucid.Site.Product;
  shouldHideProductSelect?: boolean;
}

const EmotionLayoutContextProvider = styled(LayoutContext.Provider)({});

const LearningCampusLayout: React.FC<PropsWithChildren<LearningCampusLayoutProps>> = ({
  children,
  nodeTranslations,
  shortCode,
  urlLangPrefix,
  localeId,
  pageType,
  product,
  shouldHideProductSelect = false,
}) => {
  const { name } = getLanguage(shortCode);
  const productSelectMenu = useNavMenu(3, shortCode);
  const zendeskFooterMenu = useNavFooterMenu(2, shortCode);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_footer_language_switch'],
    shortCode,
  });
  const { title } = useSiteMetadata();
  const announcement = useAnnouncementBanner(shortCode, product);

  const productSelectOptions = productSelectMenu?.map(item => {
    const link = item.link;

    return {
      label: link?.default_link_text || '',
      value: link?.final_url,
    };
  });

  const selectedProduct = productSelectOptions?.find(x => x.label.includes(product)) || productSelectOptions?.[0];
  const layoutContext = {
    product,
    shortCode,
    urlLangPrefix,
    localeId,
    pageType,
  };

  const handleProductSelectChange = (value: string) => {
    if (value && window.location?.pathname !== value) {
      window.location.assign(`${window.location?.origin}${value}`);
    }
  };

  return (
    <EmotionLayoutContextProvider value={layoutContext}>
      <GlobalCSS />
      <Pingdom id="pingdom-check" />
      <ThemeProvider product={product} theme={theme}>
        <NavBlogProvider>
          <LearningCampusNav
            buttonAriaLabel={withSmartling('mktg_footer_language_switch', {
              language: name,
            })}
            languageKey={shortCode}
            logo={{
              imageSrc: logo,
              imageAlt: `Go to ${title}'s Learning Campus homepage`,
            }}
            product={product}
          />
          <Box as="main" className="gatsby-root" data-lang={shortCode} data-page-type={pageType}>
            {announcement && <AnnouncementBanner announcement={announcement} />}
            {!shouldHideProductSelect && (
              <Container
                as="header"
                css={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  marginTop: theme.space.s64,
                  [theme.breakpoints.bp500.min]: {
                    marginTop: theme.space.s16,
                  },
                  [theme.breakpoints.bp900.min]: {
                    flexDirection: 'row',
                  },
                }}
              >
                <Combobox
                  css={{ width: '240px', maxWidth: '100%', flexBasis: 'auto', flexGrow: 0 }}
                  id="product-select"
                  onChange={optionSelected => handleProductSelectChange(optionSelected.value)}
                >
                  <Combobox.Box variant="thickBottomLine">
                    <Combobox.Input
                      hasCarot
                      aria-label="Selecting a product from the dropdown will take you to that product's Learning Campus"
                      startValue={selectedProduct?.label}
                    />
                  </Combobox.Box>
                  <Combobox.List variant="blueHighlight">
                    {productSelectOptions?.map(product => (
                      <Combobox.Option key={product.value} text={product.label} value={product.value!}>
                        <Link
                          css={theme => ({ display: 'block', fontWeight: theme.fontWeights.fw400 })}
                          href={product.value}
                          onClick={e => e.preventDefault()}
                        >
                          <Combobox.Option.Text />
                        </Link>
                      </Combobox.Option>
                    ))}
                  </Combobox.List>
                </Combobox>
                {/* TODO: Uncomment once search functionality is built */}
                {/* <SearchBar placeholder="Search Resources" /> */}
              </Container>
            )}
            {children}
          </Box>
          <SiteFooter menu={zendeskFooterMenu || []} nodeTranslations={nodeTranslations} />
        </NavBlogProvider>
      </ThemeProvider>
    </EmotionLayoutContextProvider>
  );
};

export default LearningCampusLayout;
